/**
 * Bridge to the Flutter host that wraps this web app in a WebView.
 *
 * The host exposes `window.FlutterChannel.postMessage(message: string)` and
 * invokes one of the global callbacks below with the result:
 *   - openFileSelector → window.onFlutterFileSelected(base64String, mimeType, fileName)
 *   - openCamera       → window.onFlutterCameraResult(qrText) — Flutter scans QR natively
 *                                                                and returns the decoded text
 */

type FlutterChannel = { postMessage: (msg: string) => void };

export interface FlutterFileResult {
  base64String: string;
  mimeType: string;
  fileName: string;
}

type FlutterCallbacks = {
  FlutterChannel?: FlutterChannel;
  onFlutterFileSelected?: (base64String: string, mimeType: string, fileName: string) => void;
  onFlutterCameraResult?: (qrText: string) => void;
};

const getFlutterWindow = (): (Window & FlutterCallbacks) | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window as unknown as Window & FlutterCallbacks;
};

export const isFlutterWebView = (): boolean => {
  const w = getFlutterWindow();

  return Boolean(w?.FlutterChannel?.postMessage);
};

export const openFlutterFileSelector = (
  onResult: (result: FlutterFileResult | null) => void
): boolean => {
  const w = getFlutterWindow();

  if (!w?.FlutterChannel?.postMessage) {
    return false;
  }

  w.onFlutterFileSelected = (base64String, mimeType, fileName) => {
    delete w.onFlutterFileSelected;

    if (!base64String) {
      onResult(null);

      return;
    }

    onResult({ base64String, mimeType, fileName });
  };

  w.FlutterChannel.postMessage('openFileSelector');

  return true;
};

export const flutterBase64ToFile = (
  base64String: string,
  mimeType: string,
  fileName: string
): File => {
  const cleaned = base64String.includes(',') ? base64String.split(',')[1] : base64String;
  const binary = atob(cleaned);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], fileName, { type: mimeType });
};

// Detects only explicit URL schemes. We intentionally do NOT match a bare leading `/`:
// JPEG base64 always begins with `/9j/` (encoding of the 0xFFD8FF JPEG SOI marker),
// so a `/`-prefix heuristic would route every JPEG into `fetch()` and fail.
const looksLikePathOrUrl = (value: string): boolean =>
  /^(?:file:|content:|blob:|data:|https?:)/i.test(value);

/**
 * Tolerates two Flutter delivery modes for backwards compatibility:
 *   - `base64String` is real base64 (new contract) → decoded inline
 *   - `base64String` is actually a file path / URL (legacy) → fetched
 */
export const flutterResultToFile = async (result: FlutterFileResult): Promise<File> => {
  const { base64String, mimeType, fileName } = result;
  const name = fileName || 'upload';

  if (looksLikePathOrUrl(base64String)) {
    const response = await fetch(base64String);
    const blob = await response.blob();
    const type = mimeType || blob.type || 'application/octet-stream';

    return new File([blob], name, { type });
  }

  return flutterBase64ToFile(base64String, mimeType || 'application/octet-stream', name);
};

export const openFlutterCamera = (
  onResult: (qrText: string) => void,
  onCancel?: () => void
): boolean => {
  const w = getFlutterWindow();

  if (!w?.FlutterChannel?.postMessage) {
    return false;
  }

  let settled = false;
  let wentHidden = false;
  let cancelTimer: ReturnType<typeof setTimeout> | null = null;

  const cleanup = () => {
    settled = true;
    document.removeEventListener('visibilitychange', handleVisibility);

    if (cancelTimer) {
      clearTimeout(cancelTimer);
      cancelTimer = null;
    }

    delete w.onFlutterCameraResult;
  };

  // Flutter native scanner doesn't notify the WebView when the user dismisses it
  // without scanning. The WebView is hidden while the scanner is on screen and
  // becomes visible again when it closes — if no result arrives shortly after,
  // treat the return-to-foreground as a cancel (distinct from a real empty scan).
  function handleVisibility() {
    if (document.visibilityState === 'hidden') {
      wentHidden = true;

      if (cancelTimer) {
        clearTimeout(cancelTimer);
        cancelTimer = null;
      }

      return;
    }

    if (!wentHidden) {
      return;
    }

    cancelTimer = setTimeout(() => {
      if (settled) {
        return;
      }

      cleanup();
      onCancel?.();
    }, 700);
  }

  w.onFlutterCameraResult = (qrText: string) => {
    if (settled) {
      return;
    }

    cleanup();
    onResult(qrText);
  };

  document.addEventListener('visibilitychange', handleVisibility);
  w.FlutterChannel.postMessage('openCamera');

  return true;
};
